<template>

    <cms-view>

        <div v-if="logger">
            <cms-heading :title="pageTitle">
                <cms-button icon="magicon-pencil" :to="{ name: 'logger-edit', params: { id: logger.id }}" v-if="userIsAdmin">Wijzigen</cms-button>
            </cms-heading>

            <div v-spinner="loading">
                <cms-view-panel>
                    <tr>
                        <td class="key">Serienummer</td>
                        <td>{{ logger.serialNumber }}</td>
                    </tr>
                    <tr>
                        <td class="key">Type</td>
                        <td>{{ logger.type.title }}</td>
                    </tr>
                    <tr>
                        <td class="key">Eigendom</td>
                        <td>{{ logger.owner }}</td>
                    </tr>
                    <tr>
                        <td class="key">Document referentienummer</td>
                        <td>{{ logger.documentReference }}</td>
                    </tr>
                    <tr>
                        <td class="key">Datum van aanschaf</td>
                        <td>{{ logger.purchaseDate | date('LL') }}</td>
                    </tr>
                    <tr>
                        <td class="key">Aanschafwaarde</td>
                        <td>&euro; {{ logger.purchaseValue | number }}</td>
                    </tr>
                    <tr>
                        <td class="key">Status</td>
                        <td><cms-text-label :title="logger.state | loggerState" :color="logger.state | loggerStateColor" /></td>
                    </tr>
                    <tr>
                        <td class="key">Opmerkingen</td>
                        <td>{{ logger.comments }}</td>
                    </tr>
                </cms-view-panel>

                <cms-heading v-if="logger.activeAllocation" title="Ingezet project" level="2"></cms-heading>
                <cms-view-panel v-if="logger.activeAllocation">
                    <tr>
                        <td class="key">Project</td>
                        <td>{{ logger.activeAllocation.project.title }} ({{ logger.activeAllocation.project.code }})</td>
                    </tr>
                    <tr>
                        <td class="key">Startdatum</td>
                        <td>{{ logger.activeAllocation.startDate | date('LL') }}</td>
                    </tr>
                    <tr>
                        <td class="key">Einddatum</td>
                        <td>{{ logger.activeAllocation.endDate | date('LL') }}</td>
                    </tr>
                    <tr>
                        <td class="key">Afhandelingstermijn actie</td>
                        <td>{{ logger.activeAllocation.actionDeadline | actionDeadline(logger.activeAllocation.project.actionDeadline) }} dagen</td>
                    </tr>
                </cms-view-panel>

            </div>

            <logger-action-grid :loggers="[logger]" :disabled="!!loading" @executed="onActionExecuted"></logger-action-grid>

            <cms-heading :title="'Acties (' + logger.actions.items.length + ')'" level="2"></cms-heading>
            <cms-table-panel
                :placeholder="!logger.actions.items.length"
                clickable="true"
                v-spinner="loading"
            >
                <cms-table-header>
                    <th>Projectcode</th>
                    <th>Status</th>
                    <th>Datum probleem</th>
                    <th>Categorie&euml;n</th>
                    <th><span>Opm. kantoor</span></th>
                    <th></th>
                </cms-table-header>
                <tr v-for="action in logger.actions.items" @click="showAction(action)">
                    <td>{{ action.project.code }}</td>
                    <td><cms-text-label :title="action.state | actionState" :color="action.state | actionStateColor" /></td>
                    <td>{{ action.issueDate | date('LL') }}</td>
                    <td>{{ parseCategories(action.categories) }}</td>
                    <td>{{ action.commentsOffice }}</td>
                    <td class="actions">
                        <cms-link icon="entypo-eye" v-tooltip="'Bekijken'" />
                    </td>
                </tr>
            </cms-table-panel>

            <cms-heading title="Mutatielog" level="2"></cms-heading>
            <cms-table-panel :placeholder="!logger.loggerMutations.items.length" v-spinner="loading">
                <cms-table-header>
                    <cms-th field="createdAt" v-model="mutationsSort">Datum</cms-th>
                    <cms-th field="type" v-model="mutationsSort">Mutatie-type</cms-th>
                    <cms-th>Project</cms-th>
                    <cms-th>Extra informatie</cms-th>
                    <cms-th></cms-th>
                </cms-table-header>
                <tr v-for="mutation in logger.loggerMutations.items">
                    <td>{{ mutation.createdAt | date('LL') }}</td>
                    <td><cms-text-label :title="mutation.type | loggerMutationType" :color="mutation.type | loggerMutationTypeColor" /></td>
                    <td>{{ getMutationProject(mutation) }}</td>
                    <td class="list" v-if="mutationHasFixedComments(mutation) || mutation.comments">
                        <ul>
                            <li v-for="comment in getMutationFixedComments(mutation)">{{ comment }}</li>
                            <li v-if="mutation.comments">{{ mutation.comments }}</li>
                        </ul>
                    </td>
                    <td class="actions">
                        <cms-link v-if="mutationHasAction(mutation)" icon="magicon-jump-right-up" v-tooltip="getMutationActionTooltip(mutation)" @click="onMutationActionClick(mutation)" />
                    </td>
                </tr>
            </cms-table-panel>
        </div>
    </cms-view>

</template>

<script>

    import Component from 'vue-class-component'
    import Vue from 'global-vue'

    import dateFilter from '@wittignl/js-core/filters/date'
    import actionCategoryFilter from 'app/filters/actionCategory'

    import LoggerQueries from 'app/queries/LoggerQueries'
    import LoggerMutationType from 'app/enums/LoggerMutationType'
    import UserRole from 'app/enums/UserRole'

    @Component({

        name: 'view-logger',
        apollo: {

            logger: LoggerQueries.find({

                fetchPolicy: 'cache-and-network',

                variables(){

                    return {
                        loggerId: this.loggerId,
                        mutationSortField: this.mutationsSort.field,
                        mutationSortDirection: this.mutationsSort.direction
                    }
                }
            })
        }
    })
    export default class extends Vue
    {
        data(){

            return {

                loading: 0,
                logger: null,
                mutationsSort: {
                    field: 'createdAt',
                    direction: 'DESC'
                }
            }
        }

        breadcrumb(){

            return [{
                title: 'Loggers',
                route: {
                    name: 'loggers'
                }
            }, this.pageTitle];
        }

        get pageTitle(){

            return this.logger ? `Logger ${this.logger.serialNumber}` : ''
        }

        get loggerId(){

            return this.$route.params.id;
        }

        get userIsAdmin(){

            return this.$user.role == UserRole.ADMIN;
        }

        onQueryError(){

            this.$toast.error('De logger kon niet worden opgehaald');
        }

        showAction(action){

            this.$router.push({
                name: 'action-view',
                params: { id: action.id }
            });
        }

        mutationHasAction(mutation){

            return [LoggerMutationType.DEPLOYED, LoggerMutationType.REPAIR_ACTIVE].indexOf(mutation.type) != -1;
        }

        getMutationActionTooltip(mutation){

            if(mutation.type == LoggerMutationType.DEPLOYED){
                return 'Project bekijken';
            }
            else if(mutation.type == LoggerMutationType.REPAIR_ACTIVE){
                return 'Reparatie bekijken';
            }

            return '';
        }

        onMutationActionClick(mutation){

            if(mutation.type == LoggerMutationType.DEPLOYED){

                const projectId = mutation.data.projectId;
                this.$router.push({
                    name: 'project-edit',
                    params: { id: projectId }
                });
            }
            else if(mutation.type == LoggerMutationType.REPAIR_ACTIVE){

                const repairId = mutation.data.repairId;
                this.$router.push({
                    name: 'repair-view',
                    params: { id: repairId }
                });
            }
        }

        mutationHasFixedComments(mutation){

            return true;
        }

        getMutationFixedComments(mutation){

            const data = mutation.data;

            if(mutation.type == LoggerMutationType.DEPLOYED){

                const start = dateFilter(data.startDate, 'LL');
                const end = dateFilter(data.endDate, 'LL');

                var comments = [`Project: ${data.projectCode || '-'}`];

                if(end){
                    comments.push(data.endDate ? `${start} t/m ${end}` : start);
                }
                else {
                    comments.push(`Vanaf ${start}`);
                }

                return comments;
            }
            else if(mutation.type == LoggerMutationType.REPAIR_ACTIVE){

                return [`Reparatie-batch: ${data.reference}`];
            }

            const start = dateFilter(data.startDate, 'LL');
            return [`Vanaf ${start}`];
        }

        getMutationProject(mutation){

            const data = mutation.data;

            if(mutation.type == LoggerMutationType.DEPLOYED){
                return data.projectCode;
            }

            return ''
        }

        onActionExecuted(){

            this.refetchQuery('logger');
        }

        parseCategories(categories){

            const formattedCategories = categories.map(actionCategoryFilter);
            return formattedCategories.join(', ');
        }
    }

</script>
